import {
    ClarityFormValues,
    claritySchema,
    TrackingInfos,
    TrackingType
} from "@/app/stores/[id]/storefront/types/tracking";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ClarityFormProps {
  trackingConfig: TrackingInfos<TrackingType.CLARITY>;
  defaultValues: ClarityFormValues;
  isLoading: boolean;
  savePixelConfig: (data: ClarityFormValues) => void;
}

/**
 * Microsoft Clarity is keyed on a project ID rather than a pixel or container
 * ID, and it has no ads-conversion settings — hence its own form rather than a
 * branch inside GTMForm.
 */
function ClarityForm({
  trackingConfig,
  defaultValues,
  isLoading,
  savePixelConfig,
}: ClarityFormProps) {
  const { t } = useTranslation('storefront');
  const form = useForm<ClarityFormValues>({
    resolver: zodResolver(claritySchema),
    defaultValues: defaultValues,
  });
  const { register, formState: { errors }, watch, setValue } = form;

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="enabled" className="text-sm font-medium">
            {t('tracking.clarity.enable', { name: trackingConfig.name })}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('tracking.clarity.enableDescription')}
          </p>
        </div>
        <Switch
          id="enabled"
          checked={watch('enabled')}
          onCheckedChange={(checked) => {
            setValue('enabled', checked, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            savePixelConfig({ ...watch(), enabled: checked });
          }}
          disabled={isLoading}
        />
      </div>
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">{t('tracking.clarity.projectConfiguration')}</h3>
        <div className="space-y-2">
          <Label htmlFor="projectId">
            {t('tracking.clarity.projectId')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="projectId"
            placeholder={trackingConfig.projectIdPlaceholder}
            disabled={isLoading}
            {...register('projectId')}
            onChange={(e) => {
              const value = e.target.value;
              setValue('projectId', value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
              savePixelConfig({ ...watch(), projectId: value });
            }}
          />
          <p className="text-xs text-muted-foreground">
            {trackingConfig.projectIdDescription}
          </p>
          {errors.projectId && (
            <p className="text-xs text-destructive">{errors.projectId.message}</p>
          )}
        </div>
      </div>
      {trackingConfig.events && <div className="space-y-4">
        <h3 className="text-sm font-semibold">{t('tracking.clarity.eventTracking')}</h3>
        <p className="text-xs text-muted-foreground">
          {t('tracking.clarity.eventTrackingDescription')}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {trackingConfig.events.map((event) => {
            const eventKey = event.key;
            return (
              <div key={String(event.key)} className="flex items-center space-x-2">
                <Checkbox
                  id={`event-${event.key}`}
                  checked={watch(`events.${eventKey}` as any)}
                  onCheckedChange={(checked) => {
                    setValue(`events.${eventKey}` as any, checked as boolean);
                    savePixelConfig({ ...watch(), events: { ...watch('events'), [eventKey]: checked } });
                  }}
                  disabled={isLoading}
                />
                <Label htmlFor={`event-${event.key}`} className="cursor-pointer font-normal">
                  {event.label}
                </Label>
              </div>
            );
          })}
        </div>
      </div>}
    </>
  );
}

export default ClarityForm;
